<script setup lang="ts">
import { ref, watch, watchEffect } from "vue";
import { parseNumber, throttle } from "@zyzgroup/core-common";
import { __color, getCSSVar, ColorPattern } from "@zyzgroup/core-web";
import { context, getColors, mapState } from "../../context/context";
import Icon from "./Icon.vue";
import Loading11 from "../loading/Loading11.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Icon },
  inheritAttrs: true
});

export type ButtonModePattern =
  | "primary"
  | "secondary"
  | "normal"
  | "normal-reverse"
  | "normal-transparent"
  | "outline"
  | "transparent"
  | "gradient"
  | "empty";

const props = withDefaults(
  defineProps<{
    mode?: ButtonModePattern;
    colorPattern?: ColorPattern;
    size?: "primary" | "secondary" | "tertiary";
    disabled?: boolean;
    capsule?: boolean; // 按钮的两端为半圆形，整体形状接近一个椭圆或者圆形的胶囊

    // 覆盖性 设置
    backgroundColor?: string;

    // 特异性 设置
    icon?: string;
    iconPosition?: "left" | "right";
    width?: string;
    height?: string;
    fontSize?: string;
    padding?: string;
    borderRadius?: string; // border-radius:x/y
    fontWeight?: string;
  }>(),
  {
    mode: "primary",
    colorPattern: context.store.getState().color
      .colorPattern as unknown as ColorPattern,
    size: "primary",
    disabled: false,
    capsule: false,

    // 特异性 设置
    iconPosition: "left",
    width: "auto",
    fontWeight: "normal" // normal bold
  }
);

const emits = defineEmits<{
  (event: "click", e: Event): void;
  (event: "throttledClick", e: Event): void; // for ce element
}>();

let height = props.height;
let fontSize = props.fontSize;
let padding = props.padding;
let borderRadius = props.borderRadius;
if (props.size == "primary") {
  height = height || getCSSVar("--yz-button-height", "44px");
  fontSize = fontSize || getCSSVar("--yz-font-size", "16px");
  padding = padding || getCSSVar("--yz-button-padding-horizontal", "20px");
  borderRadius = borderRadius || getCSSVar("--yz-button-border-radius", "4px");
} else if (props.size == "secondary") {
  height = height || getCSSVar("--yz-button-height-secondary", "32px");
  fontSize = fontSize || getCSSVar("--yz-font-size-secondary", "14px");
  padding =
    padding || getCSSVar("--yz-button-padding-horizontal-secondary", "16px");
  borderRadius =
    borderRadius || getCSSVar("--yz-button-border-radius-secondary", "4px");
} else if (props.size == "tertiary") {
  height = height || getCSSVar("--yz-button-height-tertiary", "24px");
  fontSize = fontSize || getCSSVar("--yz-font-size-secondary", "14px");
  padding =
    padding || getCSSVar("--yz-button-padding-horizontal-tertiary", "8px");
  borderRadius =
    borderRadius || getCSSVar("--yz-button-border-radius-tertiary", "4px");
}

const el = ref<HTMLElement>();

const opacity = ref("1");
watch(
  () => props.disabled,
  () => {
    opacity.value = props.disabled
      ? `${1 - parseNumber(getCSSVar("--yz-disabled-alpha"), 0.4)}`
      : "1";
  },
  { immediate: true }
);

// 由 <更改颜色> 变为 <添加遮罩>
// const darkenOver = 0.03;
// const darkenClick = 0.06;
// let baseBackgroundColor = backgroundColor.value;

const transparentColor = "rgba(0,0,0,0)";
// 移动: 添加遮罩
let moveCoverColor = "rgba(0,0,0,0.05)";
// 点击: 添加遮罩
let clickedCoverColor = "rgba(0,0,0,0.1)";

const showCover = ref(false);
const coverColor = ref(transparentColor);

const backgroundColor = ref(transparentColor);
const outlineColor = ref(transparentColor);
const contentColor = ref(transparentColor);

function changeColor() {
  if (isThemeDark) {
    moveCoverColor = "rgba(255,255,255,0.05)";
    clickedCoverColor = "rgba(255,255,255,0.1)";
  } else {
    moveCoverColor = "rgba(0,0,0,0.05)";
    clickedCoverColor = "rgba(0,0,0,0.1)";
  }

  const {
    panelReverse,
    panelCommon,
    gapReverse,
    gapCommon,
    fontReverse,
    fontCommon,
    primaryPattern,
    secondaryPattern,
    gradientPattern,
    foregroundPattern
  } = getColors(props.colorPattern);

  switch (props.mode) {
    case "primary":
      backgroundColor.value = primaryPattern;
      outlineColor.value = primaryPattern;
      contentColor.value = foregroundPattern;
      break;
    case "secondary":
      backgroundColor.value = secondaryPattern;
      outlineColor.value = primaryPattern;
      contentColor.value = isThemeDark ? foregroundPattern : primaryPattern;
      break;
    case "normal":
      backgroundColor.value = panelCommon;
      outlineColor.value = gapCommon;
      contentColor.value = fontCommon;
      break;
    case "normal-reverse":
      backgroundColor.value = panelReverse;
      outlineColor.value = gapReverse;
      contentColor.value = fontReverse;
      break;
    case "normal-transparent":
      backgroundColor.value = transparentColor;
      outlineColor.value = transparentColor;
      contentColor.value = fontReverse;
      break;
    case "outline":
      backgroundColor.value = transparentColor;
      outlineColor.value = primaryPattern;
      contentColor.value = primaryPattern;
      break;
    case "transparent":
      backgroundColor.value = transparentColor;
      outlineColor.value = transparentColor;
      contentColor.value = primaryPattern;
      break;
    case "gradient":
      backgroundColor.value = `linear-gradient(180deg, ${primaryPattern} 0%, ${gradientPattern} 100%)`;
      outlineColor.value = transparentColor;
      contentColor.value = foregroundPattern;
      break;
  }
  // 覆盖性 设置
  if (props.backgroundColor) {
    backgroundColor.value = props.backgroundColor;
  }
  // baseBackgroundColor = backgroundColor.value;
}

const mappedState = mapState(["theme.theme"]);
let currentTheme;
let isThemeDark;
watchEffect(() => {
  const nextTheme = mappedState["theme.theme"].value;
  if (currentTheme == nextTheme) {
    return;
  }
  currentTheme = nextTheme;
  isThemeDark = currentTheme == "dark";
  changeColor();
});

watch(
  () => props.mode,
  () => {
    changeColor();
  },
  {
    // immediate: true
  }
);

let isMouseOver = false;
const mouseover = () => {
  isMouseOver = true;
  if (props.disabled) {
    return;
  }
  showCover.value = true;
  coverColor.value = moveCoverColor;
  // switch (props.mode) {
  //   case "gradient":
  //     if (isThemeDark) {
  //       backgroundColor.value = `linear-gradient(180deg, ${__color
  //         .set(primaryPattern)
  //         .lighten(darkenOver)
  //         .toString()} 0%, ${__color
  //         .set(gradientPattern)
  //         .lighten(darkenOver)
  //         .toString()} 100%)`;
  //     } else {
  //       backgroundColor.value = `linear-gradient(180deg, ${__color
  //         .set(primaryPattern)
  //         .darken(darkenOver)
  //         .toString()} 0%, ${__color
  //         .set(gradientPattern)
  //         .darken(darkenOver)
  //         .toString()} 100%)`;
  //     }
  //     break;
  //   default:
  //     if (isThemeDark) {
  //       backgroundColor.value = __color
  //         .set(baseBackgroundColor)
  //         .lighten(darkenOver)
  //         .toString();
  //     } else {
  //       backgroundColor.value = __color
  //         .set(baseBackgroundColor)
  //         .darken(darkenOver)
  //         .toString();
  //     }
  //     break;
  // }
};

const mouseleave = () => {
  isMouseOver = false;
  showCover.value = false;
  coverColor.value = transparentColor;
  // backgroundColor.value = baseBackgroundColor;
};

const throttledClick = throttle((e: Event) => {
  if (props.disabled) {
    return;
  }
  showCover.value = true;
  coverColor.value = clickedCoverColor;
  // switch (props.mode) {
  //   case "gradient":
  //     if (isThemeDark) {
  //       backgroundColor.value = `linear-gradient(180deg, ${__color
  //         .set(primaryPattern)
  //         .lighten(darkenClick)
  //         .toString()} 0%, ${__color
  //         .set(gradientPattern)
  //         .lighten(darkenClick)
  //         .toString()} 100%)`;
  //     } else {
  //       backgroundColor.value = `linear-gradient(180deg, ${__color
  //         .set(primaryPattern)
  //         .darken(darkenClick)
  //         .toString()} 0%, ${__color
  //         .set(gradientPattern)
  //         .darken(darkenClick)
  //         .toString()} 100%)`;
  //     }
  //     break;
  //   default:
  //     if (isThemeDark) {
  //       backgroundColor.value = __color
  //         .set(baseBackgroundColor)
  //         .lighten(darkenClick)
  //         .toString();
  //     } else {
  //       backgroundColor.value = __color
  //         .set(baseBackgroundColor)
  //         .darken(darkenClick)
  //         .toString();
  //     }
  //     break;
  // }
  setTimeout(() => {
    mouseleave();
  }, 150);
  emits("click", e);
  emits("throttledClick", e);
}, 300);

defineExpose({
  // 父组件ref子Button组件时：xxx.value.el.getBoundingClientRect()
  el
});
</script>

<template>
  <div
    ref="el"
    class="button"
    :style="
      props.mode == 'empty'
        ? {
            position: 'relative',
            overflow: 'hidden',
            userSelect: 'none',
            boxSizing: 'border-box',
            display: 'inline-block',
            opacity: opacity,
            cursor: `${props.disabled ? 'not-allowed' : 'pointer'}`
          }
        : {
            position: 'relative',
            overflow: 'hidden',
            background: `${backgroundColor}`,
            border: `1px solid ${outlineColor}`,
            color: contentColor,
            opacity: opacity,
            borderRadius: `${props.capsule ? '9999px' : borderRadius}`,
            width: props.width,
            height: height,
            lineHeight: height,
            fontSize: fontSize,
            cursor: `${props.disabled ? 'not-allowed' : 'pointer'}`,
            /* `pointer-events: ${props.disabled ? 'none' : 'auto'}`, */
            userSelect: 'none',
            boxSizing: 'border-box',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: '0'
          }
    "
    @mouseover="mouseover"
    @mouseleave="mouseleave"
    @click="throttledClick"
  >
    <div
      class="truncate"
      :style="
        props.mode == 'empty'
          ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }
          : {
              padding: '0 ' + padding,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: props.fontWeight
            }
      "
    >
      <!-- <div
        class="loading"
        style="margin-right: 6px"
      >
        <Loading11 :color="contentColor"></Loading11>
      </div> -->
      <Icon
        v-if="icon && iconPosition === 'left'"
        :icon="icon"
        :color="contentColor"
        :style="{
          marginRight: '4px'
        }"
      ></Icon>
      <slot>
        <!-- default none text -->
      </slot>
      <Icon
        v-if="icon && iconPosition === 'right'"
        :icon="icon"
        :color="contentColor"
        :style="{
          marginLeft: '4px'
        }"
      ></Icon>
    </div>
    <div
      :style="[
        `display: ${showCover ? 'block' : 'none'}`,
        `position: absolute`,
        `top: 0`,
        `left: 0`,
        `width: 100%`,
        `height: 100%`,
        `background: ${coverColor}`,
        `z-index: 1`
      ]"
    ></div>
  </div>
</template>
